import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { faPlus, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { StudioService } from '../../services/studio/studio.service';
import { AuthService } from '../../services/auth/auth.service';
import { StorageService } from '../../services/storage/storage.service';
import { StorageKeys } from '../../shared/enums/storage.enum';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

interface Category {
  id: string;
  name: string;
  description?: string;
  tags: any[];
  keywords: string[];
  modelsCount: number;
  updatedAt: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {

  categoryNameControl: FormControl<string> = new FormControl('', { nonNullable: true });
  descriptionControl: FormControl<string> = new FormControl('', { nonNullable: true });
  formTitle: string = 'Add Category';
  metadataForms: Array<{
    labelControl: FormControl<string>;
    keywordsControl: FormControl<string>;
    keywordsList: string[];
    fieldTypeControl: FormControl<string | null >;
  }> = [];

  fieldTypeOptions = [
    { id: 'text', name: 'Text' },
    { id: 'array', name: 'Array' }
  ];

  categories: Category[] = [];
  loading: boolean = false;
  currentPage: number = 0;
  endOfResults: boolean = false;
  isSavingCategory = false;
  errorMsg: string = '';
  credits: number | null = null;
  formSubmitted: boolean = false;
  isFormOpen = false;
  faPlus = faPlus;
  faTimes = faTimes;
  faTrash = faTrash;
  faSpinner = faSpinner;

  constructor(
    private readonly studioService: StudioService,
    private readonly authService: AuthService,
    private readonly storageService: StorageService,
    public readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly router: Router,
  ) { }

 ngOnInit(): void {
    this.categories = [];
    this.route.queryParams.subscribe((params) => {
      if (params['token']) {
        try {
          const decoded = JSON.parse(atob(params['token']));
          this.storageService.setDataToLocalStorage(StorageKeys.ACCESS_TOKEN, decoded.token);
        } catch (e) {
          this.errorMsg = 'Invalid token received.';
        }
      }
      this.getSelfDetails();
      this.loadCategories();
      this.loadCredits();
      this.location.replaceState(this.router.url?.split('?')[0]);
    });
  }

  loadCredits(): void {
    this.studioService.getUserCredits().subscribe({
      next: (res) => {
        this.credits = res?.data?.credits ?? res?.data?.credit ?? res?.credits ?? res?.credit ?? null;
      },
      error: () => { this.credits = null; }
    });
  }

  getSelfDetails(): void {
    this.authService.getSelfDetails().subscribe({
      next: (res) => {
        if (res?.data) {
          this.storageService.setDataToLocalStorage(StorageKeys.SELF_DETAILS, res.data);
          this.authService.setUser(res.data);
        }
      },
      error: (err) => {
        console.error('Failed to fetch self details:', err);
      }
    });
  }

  get isSaveButtonDisabled(): boolean {
    if (this.isSavingCategory) {
      return true;
    }
    const hasCategoryName = this.categoryNameControl?.value?.trim?.() && this.categoryNameControl.value.trim().length > 0;
    const hasValidForm = this.metadataForms.some(form => {
      const hasLabel = form.labelControl?.value?.trim?.() && form.labelControl.value.trim().length > 0;
      const hasFieldType = form.fieldTypeControl?.value && form.fieldTypeControl.value.length > 0;
      
      return hasLabel && hasFieldType;
    });

    // Check if at least one metadata form has keywords
    const hasKeywords = this.metadataForms.some(form => form.keywordsList && form.keywordsList.length > 0);

    // If formSubmitted is true and fields are empty, keep button disabled
    if (this.formSubmitted && (!hasCategoryName || !hasValidForm || !hasKeywords)) {
      return true;
    }

    return !hasCategoryName || !hasValidForm || !hasKeywords;
  }

  saveCategory(): void {
    this.formSubmitted = true;
    this.errorMsg = '';
    this.categoryNameControl.markAsTouched();

    if (!this.categoryNameControl.value.trim()) {
      return;
    }

    const validMetadata = this.metadataForms
      .filter(form => {
        const hasLabel = form.labelControl?.value?.trim?.();
        const hasFieldType = form.fieldTypeControl?.value;
        return hasLabel && hasFieldType;
      })
      .map(form => ({
        label: form.labelControl.value.trim(),
        fieldType: form.fieldTypeControl.value!
      }));

    const keywords = this.metadataForms
      .filter(form => {
        const hasLabel = form.labelControl?.value?.trim?.();
        const hasFieldType = form.fieldTypeControl?.value;
        return hasLabel && hasFieldType;
      })
      .flatMap(form => form.keywordsList);

    if (validMetadata.length === 0) {
      return;
    }

    const payload = {
      metadata: validMetadata,
      keywords: keywords,
      category: this.categoryNameControl.value.trim(),
      Prompt: this.descriptionControl.value.trim()
    };

    console.log('Sending payload:', payload);

    this.isSavingCategory = true;
    this.studioService.createStudioMetadata(payload).subscribe({
      next: () => {
        this.isSavingCategory = false;
        this.currentPage = 0;
        this.categories = [];
        this.endOfResults = false;
        this.loadCategories();
        this.closeCategoryForm();
      },
      error: (error) => {
        this.isSavingCategory = false;
        this.errorMsg = error?.error?.error?.message || error?.error?.message || error?.message || 'Category not saved. Something went wrong!';
      },
    });
  }

  resetForm(): void {
    this.categoryNameControl.setValue('');
    this.descriptionControl.setValue('');
    this.metadataForms = [];
    this.addNewMetadataForm();
  }

  onDescriptionFocus(): void {
    this.formSubmitted = false;
  }

  onDescriptionBlur(): void {
    this.descriptionControl.markAsTouched();
  }

  loadCategories(): void {
    this.loading = true;
    this.studioService.getStudioMetadata({ page: this.currentPage }).subscribe({
      next: (response) => {
        const items = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];
        const categories = items.map((item: any, index: number) => {
          // Store complete metadata objects for display
          const metadataList = Array.isArray(item?.metadata) 
            ? item.metadata
            : [];
          const keywordsList = Array.isArray(item?.keywords)
            ? item.keywords
            : [];
          return {
            id: item?._id,
            name: item?.category || item?.name || 'Untitled',
            tags: metadataList,
            keywords: keywordsList,
            modelsCount: Number(item?.modelsCount || 0),
            updatedAt: item?.updated || item?.created || item?.updatedAt || item?.createdAt || '-',
          };
        });
        
        if (this.currentPage === 0) {
          this.categories = categories;
        } else {
          this.categories = [...this.categories, ...categories];
        }  
        this.loading = false;
        if (categories.length < 20) {
          this.endOfResults = true;
        }
      },
      error: (error) => {
        this.loading = false;
        this.endOfResults = false;
      },
    });
  }

  loadMoreCategories(): void {
    if (this.endOfResults) return;
    this.currentPage++;
    this.loadCategories();
  }

  trackByCategoryId(index: number, category: Category): string {
    return category.id || `${category.name}-${index}`;
  }

  closeToaster(): void {
    this.errorMsg = '';
  }

  openCategoryForm(): void {
    this.categoryNameControl.setValue('');
    this.descriptionControl.setValue('');
    this.metadataForms = [];
    this.addNewMetadataForm();
    this.formSubmitted = false;
    this.isFormOpen = true;
  }

  closeCategoryForm(): void {
    this.isFormOpen = false;
    this.formSubmitted = false;
    this.categoryNameControl.reset('', { emitEvent: false });
    this.descriptionControl.setValue('');
    this.metadataForms = [];
    this.addNewMetadataForm();
  }

  addMetadataKeyword(event?: Event, formIndex: number = 0): void {
    event?.preventDefault();
    const form = this.metadataForms[formIndex];
    const keywordValue = form.keywordsControl.value.trim();
    const normalizedKeyword = keywordValue.toLowerCase();
    if (!normalizedKeyword) {
      return;
    }
    const isDuplicate = form.keywordsList.some(kw => kw.trim().toLowerCase() === normalizedKeyword);
    if (isDuplicate) {
      form.keywordsControl.setValue('');
      return;
    }
    form.keywordsList.push(keywordValue);
    form.keywordsControl.setValue('');
    this.cdr.markForCheck();
  }

  removeMetadataKeyword(index: number, formIndex: number = 0): void {
    this.metadataForms[formIndex].keywordsList.splice(index, 1);
    this.cdr.markForCheck();
  }

  addNewMetadataForm(): void {
    const labelControl = new FormControl('', { nonNullable: true });
    const keywordsControl = new FormControl('', { nonNullable: true });
    const fieldTypeControl = new FormControl(null);

    labelControl.valueChanges.subscribe(() => this.cdr.markForCheck());
    keywordsControl.valueChanges.subscribe(() => this.cdr.markForCheck());
    fieldTypeControl.valueChanges.subscribe(() => this.cdr.markForCheck());

    this.metadataForms.push({
      labelControl,
      keywordsControl,
      keywordsList: [],
      fieldTypeControl
    });
    this.cdr.markForCheck();
  }

  removeMetadataForm(index: number): void {
    this.metadataForms.splice(index, 1);
    this.cdr.markForCheck();
  }
}
